import { useState, useCallback } from 'react';
import { bomApi } from '../services/api';
import { 
  BOMExtractionJob, 
  BOMItem, 
  BOMExtractionParams,
  BOMFilterOptions,
} from '../types/bom';
import { useToast } from './use-toast';

const defaultExtractionParams: BOMExtractionParams = {
  files: [],
  extraction_method: 'docling',
  use_llm_mapper: false,
  enable_llm_validation: true,
  enable_bom_reasoning: false,
  llm_provider: 'openai'
};

const defaultFilters: BOMFilterOptions = {};

export function useBOM() {
  const [currentJob, setCurrentJob] = useState<BOMExtractionJob | null>(null);
  const [currentBOMData, setCurrentBOMData] = useState<BOMItem[]>([]);
  const [isLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractionParams, setExtractionParams] = useState<BOMExtractionParams>(defaultExtractionParams);
  const [filters, setFilters] = useState<BOMFilterOptions>(defaultFilters);
  const { toast } = useToast();

  const submitExtractionJob = useCallback(async (params: BOMExtractionParams) => {
    if (params.files.length === 0) {
      toast({
        title: "Error",
        description: "Please select files to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const response = await bomApi.submitExtractionJob(params);
      toast({
        title: "Success",
        description: `BOM extraction job submitted successfully. Job ID: ${response.job_id}`,
      });
      
      // Set the current job
      setCurrentJob({
        job_id: response.job_id,
        status: 'pending',
        created_at: new Date().toISOString(),
        filenames: params.files.map(f => f.name),
        extraction_method: params.extraction_method,
        parameters: {
          use_llm_mapper: params.use_llm_mapper,
          enable_llm_validation: params.enable_llm_validation,
          enable_bom_reasoning: params.enable_bom_reasoning,
          llm_provider: params.llm_provider,
        },
      });
      
      // Reset form
      setExtractionParams(defaultExtractionParams);
      
      return response;
    } catch (error) {
      console.error('Error submitting BOM extraction job:', error);
      toast({
        title: "Error",
        description: "Failed to submit BOM extraction job",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, [toast]);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const latestJobData = await bomApi.getJobStatus(jobId);
      setCurrentJob(latestJobData);
      
      // Extract BOM data from job result
      if (latestJobData.status === 'completed' && latestJobData.result) {
        const bomData: BOMItem[] = [];
        latestJobData.result.results.forEach(fileResult => {
          if (fileResult.success && fileResult.bom_data) {
            fileResult.bom_data.forEach(item => {
              bomData.push({
                ...item,
                job_id: latestJobData.job_id,
                source_file: fileResult.filename
              });
            });
          }
        });
        setCurrentBOMData(bomData);
      } else if (latestJobData.status === 'failed') {
        setCurrentBOMData([]);
      }
    } catch (error) {
      console.error('Error polling job status:', error);
    }
  }, []);

  const handleExport = useCallback(async (format: 'csv' | 'excel' | 'json' | 'pdf', _options?: any) => {
    setIsExporting(true);
    try {
      // Check if there's data to export
      if (currentBOMData.length === 0) {
        toast({
          title: "Error",
          description: "No BOM data available to export",
          variant: "destructive",
        });
        return;
      }

      let blob: Blob;
      let filename: string;

      switch (format) {
        case 'csv':
          // Generate CSV from currentBOMData
          const headers = Object.keys(currentBOMData[0]).join(',');
          const rows = currentBOMData.map(item =>
            Object.values(item).map(value => {
              // Handle values that contain commas or quotes
              const stringValue = String(value ?? '');
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }).join(',')
          );
          const csvContent = [headers, ...rows].join('\n');
          blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          filename = `bom_export_${Date.now()}.csv`;
          break;

        case 'json':
          // Generate JSON from currentBOMData
          const jsonContent = JSON.stringify(currentBOMData, null, 2);
          blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
          filename = `bom_export_${Date.now()}.json`;
          break;

        case 'excel':
          // Excel export requires a library like xlsx (SheetJS)
          // For now, fall back to CSV format
          toast({
            title: "Info",
            description: "Excel export not available in frontend-only mode. Exporting as CSV instead.",
          });
          const excelCsvHeaders = Object.keys(currentBOMData[0]).join(',');
          const excelCsvRows = currentBOMData.map(item =>
            Object.values(item).map(value => {
              const stringValue = String(value ?? '');
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }).join(',')
          );
          const excelCsvContent = [excelCsvHeaders, ...excelCsvRows].join('\n');
          blob = new Blob([excelCsvContent], { type: 'text/csv;charset=utf-8;' });
          filename = `bom_export_${Date.now()}.csv`;
          break;

        case 'pdf':
          // PDF export requires a library like jsPDF
          toast({
            title: "Info",
            description: "PDF export not available in frontend-only mode. Please use CSV or JSON format.",
            variant: "destructive",
          });
          return;

        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: `BOM data exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Error exporting BOM data:', error);
      toast({
        title: "Error",
        description: "Failed to export BOM data",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [toast, currentBOMData]);

  const handleItemEdit = useCallback(async (_jobId: string, _item: BOMItem) => {
    try {
      // await bomApi.updateBOMItem(jobId, item.id!, item);
      toast({
        title: "Success",
        description: "BOM item updated successfully",
      });
      
      // Refresh current BOM data by re-polling the job
      // await pollJobStatus(jobId);
    } catch (error) {
      console.error('Error updating BOM item:', error);
      toast({
        title: "Error",
        description: "Failed to update BOM item",
        variant: "destructive",
      });
      throw error;
    }
  }, [pollJobStatus, toast]);

  const handleItemDelete = useCallback(async (jobId: string, itemId: string) => {
    try {
      await bomApi.deleteBOMItem(jobId, itemId);
      toast({
        title: "Success",
        description: "BOM item deleted successfully",
      });
      
      // Refresh current BOM data by re-polling the job
      await pollJobStatus(jobId);
    } catch (error) {
      console.error('Error deleting BOM item:', error);
      toast({
        title: "Error",
        description: "Failed to delete BOM item",
        variant: "destructive",
      });
      throw error;
    }
  }, [pollJobStatus, toast]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    currentJob,
    currentBOMData,
    isLoading,
    isUploading,
    isExporting,
    error,
    extractionParams,
    filters,
    
    // Actions
    setExtractionParams,
    setFilters,
    submitExtractionJob,
    pollJobStatus,
    handleExport,
    handleItemEdit,
    handleItemDelete,
    clearError,
  };
}
